import { inject, Injectable } from '@angular/core';
import { ConfigurationResponse } from '@api-response-types/configuration.response';
import { EventVariableName } from '@api-types/dtos/event-variable.dto';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { EventVariableHttpClient } from 'demand-forecasting/services/http/event-variable-http-client';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';

// TODO test test test
@Injectable({
  providedIn: 'root',
})
export class FeatureTranslationService {
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly eventVariableHttpClient = inject(EventVariableHttpClient);

  private readonly featureVariablePattern =
    /(date_\d+)|(categorical_\d+)|(numeric_\d+)/;
  private eventVariables: EventVariableName[] = [];
  private readonly configuration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationByUrlParam,
  );
  constructor() {
    this.eventVariableHttpClient.getEventVariablesNames().subscribe((res) => {
      this.eventVariables = res;
    });
  }

  translateFeatures(features: string[]): string[] {
    const config = this.configuration();
    if (!config) {
      // No config means we probably navigated back from a forecast result page to the configuration page
      throw new Error('No configuration found');
    }
    return features.map((feature) => {
      // normal feature column from dataset does not need to be translated
      if (!feature.startsWith('_')) {
        return feature;
      }
      if (feature.startsWith('_Lag_') || feature.startsWith('_LaggedTarget_')) {
        return this.translateLaggedTarget(feature, config);
      }
      if (feature.startsWith('_Holiday_')) {
        return this.translateHoliday(feature);
      }
      if (feature.startsWith('_Bridgeday_')) {
        return this.translateBridgeday(feature);
      }
      if (feature.startsWith('_Baseline') || feature.startsWith('_Week')) {
        return this.translateWithTranslateService(feature);
      }
      if (feature.startsWith('_Promotion_')) {
        return this.translateEvents(feature);
      }

      // prepare feature string for lookup
      // replace the feature
      const featureVariable = this.replaceFeatureVariable(feature);
      const preparedFeature =
        featureVariable.variable !== ''
          ? featureVariable.featureDescription
          : feature;
      // replace numbers
      const finalFeature = this.replaceNumericValue(preparedFeature);
      const translation = this.translateService.instant(
        `RESULT.IMPORTANCE.${finalFeature.featureDescription}`,
        {
          feature:
            featureVariable.variable !== '' ? featureVariable.variable : '',
          x: finalFeature.value,
        },
      );
      if (translation === `RESULT.IMPORTANCE.${feature}`) {
        return feature.replace(/categorical_\d+_/, '');
      }
      return translation;
    });
  }

  /**
   * Replaces a numeric in the feature with an X.
   *
   * @param featureDescription string describing the feature
   * @returns replaced feature and the value of the numeric
   */
  public replaceNumericValue(featureDescription: string): {
    featureDescription: string;
    value: number;
  } {
    const numericPattern = /([0-9]+)/g;
    const value = featureDescription.match(numericPattern);
    return {
      featureDescription: featureDescription.replace(numericPattern, 'X'),
      value: value ? +value[0] : 0,
    };
  }

  /**
   * Replace a variable in a feature with "feature" for retrieving its description
   *
   * @param featureDescriptionstring describing the feature
   * @returns replaced featureDesctiption and the variable.
   */
  public replaceFeatureVariable(featureDescription: string): {
    featureDescription: string;
    variable: string;
  } {
    const featureVariable = featureDescription.match(
      this.featureVariablePattern,
    );
    return {
      featureDescription: featureDescription.replace(
        this.featureVariablePattern,
        'feature',
      ),
      variable: featureVariable ? featureVariable[0] : '',
    };
  }

  private translateLaggedTarget(
    feature: string,
    configuration: ConfigurationResponse,
  ): string {
    try {
      const featureParts = feature.split('_');
      const lag = featureParts[2];
      let laggedName;

      if (featureParts.length > 3) {
        const columnName = featureParts.slice(3, featureParts.length).join('_');
        laggedName = columnName;
      } else {
        laggedName = configuration.startForecastConfig.target;
      }

      if (lag === '1') {
        return this.translateService.instant(
          'RESULT.IMPORTANCE._LaggedTarget_SINGULAR',
          { laggedName, lag },
        );
      }

      return this.translateService.instant(
        'RESULT.IMPORTANCE._LaggedTarget_PLURAL',
        { laggedName, lag },
      );
    } catch {
      return feature;
    }
  }

  private translateHoliday(feature: string): string {
    let holidayLabel = this.translateService.instant(
      'RESULT.IMPORTANCE.HOLIDAY',
    );
    const [country, holiday] = feature.substr(9).split('_');
    holidayLabel = `${holidayLabel} ${country}`;

    if (holiday === 'None') {
      return `${holidayLabel}: ${this.translateService.instant(
        'RESULT.IMPORTANCE.NONE',
      )}`;
    }
    if (holiday) {
      return `${holidayLabel}: ${holiday}`;
    }

    return holidayLabel;
  }

  private translateBridgeday(feature: string): string {
    let bridgedayLabel = this.translateService.instant(
      'RESULT.IMPORTANCE._Bridgeday',
    );
    const [country, bridgeDay] = feature.substr(11).split('_');

    bridgedayLabel = `${bridgedayLabel} ${country}`;

    if (bridgeDay === 'None') {
      return `${bridgedayLabel}: ${this.translateService.instant(
        'RESULT.IMPORTANCE.NONE',
      )}`;
    }
    if (bridgeDay) {
      return `${bridgedayLabel}: ${bridgeDay}`;
    }

    return bridgedayLabel;
  }

  private translateEvents(feature: string): string {
    if (feature.length > 47) {
      const eventVariableOption = feature.substring(48);
      const eventVariable =
        this.eventVariables.find((pv) => pv.id === feature.substring(11, 47))
          ?.name || feature.substring(11, 47);
      return `${eventVariable} ${eventVariableOption}`;
    }
    return (
      this.eventVariables.find(
        (eventVariable) => eventVariable.id === feature.substring(11),
      )?.name || feature
    );
  }

  private translateWithTranslateService(key: string): string {
    return this.translateService.instant(`RESULT.EXPLANATION.Y_FIELD.${key}`);
  }
}
